<template>
  <div class="email-template-editor-page flex-1 overflow-auto">
    <woot-loading-state
      v-if="uiFlags.isFetching"
      :message="$t('EMAIL_TEMPLATES.EDITOR.LOADING')"
    />
    <div v-else class="email-template-editor">
      <div class="viewer-header">
        <div class="viewer-header-main">
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-chevron-left"
            color-scheme="secondary"
            class="email-template-back-btn"
            type="button"
            @click="goBackToList"
          >
            {{ $t('EMAIL_TEMPLATES.BACK_TO_LIST') }}
          </woot-button>
          <h2 class="header-title">
            {{ pageTitle }}
          </h2>
        </div>
      </div>

      <form class="content-wrapper email-template-editor-form" @submit.prevent="submit">
        <div class="preview-header">
          <h3 class="preview-title">{{ $t('EMAIL_TEMPLATES.EDITOR.MAIN_SECTION') }}</h3>
          <div class="preview-actions">
            <woot-button
              type="button"
              variant="smooth"
              color-scheme="primary"
              icon="ai-sparkle"
              @click="openAiModal"
            >
              {{ $t('EMAIL_TEMPLATES.EDITOR.GENERATE_WITH_AI') }}
            </woot-button>
            <woot-button
              type="button"
              variant="smooth"
              color-scheme="secondary"
              icon="eye-show"
              @click="openTemplatePreview"
            >
              {{ $t('EMAIL_TEMPLATES.PREVIEW_BTN') }}
            </woot-button>
            <woot-button
              type="button"
              variant="smooth"
              color-scheme="secondary"
              @click="goBackToList"
            >
              {{ $t('EMAIL_TEMPLATES.CANCEL') }}
            </woot-button>
          </div>
        </div>

        <div class="template-panel">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.NAME_LABEL') }}</label>
          <input
            v-model="form.name"
            type="text"
            class="panel-input"
            :class="{ 'panel-input--error': $v.form.name.$error }"
            :placeholder="$t('EMAIL_TEMPLATES.EDITOR.NAME_PLACEHOLDER')"
            @blur="$v.form.name.$touch"
          />
          <span v-if="$v.form.name.$error" class="form-error">
            {{ $t('EMAIL_TEMPLATES.EDITOR.NAME_ERROR') }}
          </span>

          <label class="section-label section-label--spaced">{{
            $t('EMAIL_TEMPLATES.EDITOR.SUBJECT_LABEL')
          }}</label>
          <div class="subject-row">
            <input
              ref="subjectInput"
              v-model="form.subject"
              type="text"
              class="panel-input subject-input"
              :class="{ 'panel-input--error': $v.form.subject.$error }"
              :placeholder="$t('EMAIL_TEMPLATES.EDITOR.SUBJECT_PLACEHOLDER')"
              :maxlength="subjectMaxLength"
              @blur="$v.form.subject.$touch"
              @input="onSubjectInput"
            />
          </div>
          <div
            class="subject-char-count"
            :class="{
              'subject-char-count--warn': subjectLength > subjectDesktopRec,
              'subject-char-count--limit': subjectLength > subjectDisplayMax,
            }"
          >
            {{ subjectLength }} / {{ subjectMaxLength }}
          </div>
          <span v-if="$v.form.subject.$error" class="form-error">
            {{ $t('EMAIL_TEMPLATES.EDITOR.SUBJECT_ERROR') }}
          </span>
          <div class="add-variable-row add-variable-row--subject">
            <button
              type="button"
              class="add-variable-btn"
              :disabled="atNumericVariableLimit"
              @mousedown.prevent
              @click="addSubjectVariable"
            >
              + {{ $t('EMAIL_TEMPLATES.ADD_VARIABLE') }}
            </button>
          </div>
          <p v-if="restrictionSubject" class="body-restriction-msg">
            * Cannot add two consecutive variables
          </p>
        </div>

        <div class="template-panel template-panel--body">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.BODY_LABEL') }}</label>
          <div class="body-editor-wrap" :class="{ 'body-editor-wrap--error': $v.form.body.$error }">
            <woot-message-editor
              ref="bodyEditor"
              v-model="form.body"
              class="input message-editor email-template-body-editor"
              :is-format-mode="true"
              :min-height="6"
              :disable-autofocus="true"
              :enable-variables="false"
              :enable-canned-responses="false"
              :placeholder="$t('EMAIL_TEMPLATES.EDITOR.BODY_PLACEHOLDER')"
              @blur="$v.form.body.$touch"
              @input="onBodyInput"
            />
          </div>
          <p v-if="restrictionBody" class="body-restriction-msg">
            * Cannot add two consecutive variables
          </p>
          <span v-if="$v.form.body.$error" class="form-error">
            {{ $t('EMAIL_TEMPLATES.EDITOR.BODY_ERROR') }}
          </span>

          <div class="add-variable-row">
            <button
              type="button"
              class="add-variable-btn"
              :disabled="atNumericVariableLimit"
              @mousedown.prevent
              @click="addVariable"
            >
              + {{ $t('EMAIL_TEMPLATES.ADD_VARIABLE') }}
            </button>
          </div>
        </div>

        <div
          v-if="showVariableSamples && allVariableIndices.length > 0"
          class="template-panel variable-samples variable-samples--unified"
          :class="{ 'variable-samples--error': showSamplesError && allSamplesInvalid }"
        >
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.ALL_VARIABLE_SAMPLES') }}</label>
          <p class="variable-samples__intro">
            {{ $t('EMAIL_TEMPLATES.EDITOR.ALL_VARIABLE_SAMPLES_HINT') }}
          </p>
          <p v-if="showSamplesError && allSamplesInvalid" class="variable-samples__error-msg">
            {{ $t('EMAIL_TEMPLATES.EDITOR.SAMPLE_VARS_REQUIRED') }}
          </p>
          <div class="variable-samples__list">
            <div
              v-for="n in allVariableIndices"
              :key="'unified-var-' + n"
              class="variable-sample-row variable-sample-row--unified"
            >
              <div class="variable-sample-row__meta">
                <span class="variable-sample-label variable-sample-label--token">{{
                  variableLabel(n)
                }}</span>
                <span class="variable-sample-usage">{{ variableUsageHint(n) }}</span>
              </div>
              <input
                :value="getUnifiedSampleValue(n)"
                type="text"
                class="variable-sample-input"
                :class="{
                  'variable-sample-input--error':
                    showSamplesError && allSamplesInvalid && unifiedSampleRowInvalid(n),
                }"
                :placeholder="
                  $t('EMAIL_TEMPLATES.EDITOR.VARIABLE_SAMPLE_PLACEHOLDER', {
                    token: variableLabel(n),
                  })
                "
                @input="onUnifiedSampleInput(n, $event)"
              />
            </div>
          </div>
        </div>

        <div class="template-panel">
          <span id="email-template-visibility-label" class="section-label">{{
            $t('EMAIL_TEMPLATES.EDITOR.VISIBILITY_LABEL')
          }}</span>
          <div
            class="visibility-options"
            role="radiogroup"
            aria-labelledby="email-template-visibility-label"
          >
            <button
              type="button"
              class="visibility-option"
              :class="{ 'visibility-option--active': form.visibility === 'global' }"
              role="radio"
              :aria-checked="form.visibility === 'global'"
              @click="form.visibility = 'global'"
            >
              <span class="visibility-option__label">{{ $t('EMAIL_TEMPLATES.VISIBILITY.PUBLIC') }}</span>
              <span class="visibility-option__desc">{{
                $t('EMAIL_TEMPLATES.EDITOR.VISIBILITY_PUBLIC_DESC')
              }}</span>
            </button>
            <button
              type="button"
              class="visibility-option"
              :class="{ 'visibility-option--active': form.visibility === 'personal' }"
              role="radio"
              :aria-checked="form.visibility === 'personal'"
              @click="form.visibility = 'personal'"
            >
              <span class="visibility-option__label">{{ $t('EMAIL_TEMPLATES.VISIBILITY.PRIVATE') }}</span>
              <span class="visibility-option__desc">{{
                $t('EMAIL_TEMPLATES.EDITOR.VISIBILITY_PRIVATE_DESC')
              }}</span>
            </button>
          </div>
        </div>

        <div class="template-panel template-panel--attachments">
          <label class="section-label">{{ $t('EMAIL_TEMPLATES.EDITOR.ATTACHMENTS_LABEL') }}</label>
          <p class="form-hint">{{ $t('EMAIL_TEMPLATES.EDITOR.ATTACHMENTS_HINT') }}</p>
          <div v-if="allAttachments.length" class="attachments-list">
            <div
              v-for="att in existingAttachments"
              :key="'ex-' + att.id"
              class="attachment-item-preview"
            >
              <div class="attachment-thumbnail">
                <fluent-icon icon="document" size="22" class="file-icon text-slate-500" />
              </div>
              <span class="attachment-name">{{ att.filename }}</span>
              <button
                type="button"
                class="attachment-item__remove"
                @click="removeExistingAttachment(att.id)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </div>
            <div
              v-for="(att, index) in pendingAttachments"
              :key="'pending-' + index"
              class="attachment-item-preview"
            >
              <div class="attachment-thumbnail">
                <fluent-icon icon="document" size="22" class="file-icon text-slate-500" />
              </div>
              <span class="attachment-name">{{ att.filename }}</span>
              <button
                type="button"
                class="attachment-item__remove"
                @click="removePendingAttachment(index)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </div>
          </div>
          <input
            ref="attachmentInput"
            type="file"
            class="hidden"
            multiple
            @change="onAttachmentSelected"
          />
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            size="small"
            type="button"
            class="mt-2"
            @click="triggerAttachmentInput"
          >
            <fluent-icon icon="attach" size="14" class="mr-1" />
            {{ $t('EMAIL_TEMPLATES.EDITOR.ADD_ATTACHMENT') }}
          </woot-button>
        </div>

        <div class="bottom-actions">
          <woot-button :is-loading="uiFlags.isSaving" :disabled="uiFlags.isSaving" type="submit">
            {{ isEdit ? $t('EMAIL_TEMPLATES.SAVE_UPDATE') : $t('EMAIL_TEMPLATES.SAVE_CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>

    <woot-modal
      content-class="email-template-preview-modal-container"
      :show.sync="showPreviewModal"
      :on-close="() => (showPreviewModal = false)"
    >
      <email-template-preview-content
        v-if="showPreviewModal"
        :subject="previewSubject"
        :body-html="previewBodyHtml"
        :attachments="allAttachments"
      />
    </woot-modal>

    <woot-modal
      content-class="email-template-ai-modal-container"
      :show.sync="showAiModal"
      :on-close="closeAiModal"
    >
      <div class="ai-modal-content">
        <h3 class="ai-modal__title">{{ $t('EMAIL_TEMPLATES.EDITOR.AI_MODAL_TITLE') }}</h3>
        <label class="ai-modal__label" for="email-template-ai-query">
          {{ $t('EMAIL_TEMPLATES.EDITOR.AI_QUERY_LABEL') }}
          <span class="ai-modal__required" aria-hidden="true">*</span>
        </label>
        <textarea
          id="email-template-ai-query"
          v-model="aiQuery"
          class="ai-modal__textarea"
          rows="5"
          required
          aria-required="true"
          :placeholder="$t('EMAIL_TEMPLATES.EDITOR.AI_QUERY_PLACEHOLDER')"
          :disabled="uiFlags.isAiGenerating"
        />
        <div class="ai-modal__actions">
          <woot-button
            type="button"
            variant="smooth"
            color-scheme="secondary"
            :disabled="uiFlags.isAiGenerating"
            @click="closeAiModal"
          >
            {{ $t('EMAIL_TEMPLATES.CANCEL') }}
          </woot-button>
          <woot-button
            type="button"
            :is-loading="uiFlags.isAiGenerating"
            :disabled="uiFlags.isAiGenerating"
            @click="runAiSuggest"
          >
            {{ $t('EMAIL_TEMPLATES.EDITOR.AI_GENERATE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import accountMixin from 'dashboard/mixins/account.js';
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import EmailTemplatePreviewContent from './EmailTemplatePreviewContent.vue';

export default {
  components: { WootMessageEditor, EmailTemplatePreviewContent },
  mixins: [alertMixin, accountMixin],
  validations: {
    form: {
      name: { required },
      subject: { required },
      body: { required },
    },
  },
  data() {
    return {
      form: {
        name: '',
        subject: '',
        body: '',
        visibility: 'global',
      },
      existingAttachments: [],
      pendingAttachments: [],
      removedAttachmentIds: [],
      uiFlags: { isFetching: false, isSaving: false, isAiGenerating: false },
      showPreviewModal: false,
      showAiModal: false,
      aiQuery: '',
      showSamplesError: false,
      subjectVariables: [],
      bodyVariables: [],
      showVariableSamples: false,
      restrictionBody: false,
      restrictionSubject: false,
      previousInputBoxData: '',
      previousSubjectData: '',
    };
  },
  computed: {
    templateId() {
      const raw = this.$route.params.templateId;
      if (raw == null || raw === '' || String(raw) === 'new') {
        return undefined;
      }
      return String(raw);
    },
    isEdit() {
      return !!this.templateId;
    },
    pageTitle() {
      if (this.isEdit) {
        const name = (this.form.name || '').trim();
        return name || this.$t('EMAIL_TEMPLATES.EDITOR.PAGE_TITLE_EDIT');
      }
      return this.$t('EMAIL_TEMPLATES.EDITOR.PAGE_TITLE_NEW');
    },
    subjectLength() {
      return (this.form.subject || '').length;
    },
    subjectMaxLength() {
      return 998;
    },
    subjectDesktopRec() {
      return 70;
    },
    subjectDisplayMax() {
      return 256;
    },
    allAttachments() {
      return [...this.existingAttachments, ...this.pendingAttachments];
    },
    subjectNumericVarMax() {
      return this.getMaxNumericVarIndexFromStrings(this.form.subject, '');
    },
    bodyNumericVarMax() {
      return this.getMaxNumericVarIndexFromStrings('', this.form.body);
    },
    /** Every {{n}} used in subject or body, sorted (single list at bottom of form). */
    allVariableIndices() {
      const set = new Set([
        ...this.collectNumericVariableIndices(this.form.subject || ''),
        ...this.collectNumericVariableIndices(this.form.body || ''),
      ]);
      return Array.from(set).sort((a, b) => a - b);
    },
    atNumericVariableLimit() {
      return this.getMaxNumericVarIndex() >= 10;
    },
    allSamplesInvalid() {
      for (let i = 0; i < this.allVariableIndices.length; i += 1) {
        const n = this.allVariableIndices[i];
        if (this.unifiedSampleRowInvalid(n)) return true;
      }
      return false;
    },
    previewSubject() {
      return this.resolveVariablesWithSamples(
        this.form.subject,
        this.subjectVariables
      );
    },
    previewBodyHtml() {
      if (!this.showPreviewModal) {
        return '';
      }
      try {
        const rendered = this.resolveVariablesWithSamples(
          this.form.body,
          this.bodyVariables
        );
        return new MessageFormatter(rendered).formattedMessage;
      } catch (e) {
        return '';
      }
    },
  },
  watch: {
    subjectVariables: { deep: true, handler: 'clearSamplesErrorIfValid' },
    bodyVariables: { deep: true, handler: 'clearSamplesErrorIfValid' },
    'form.body': 'onSubjectOrBodyChanged',
    'form.subject': 'onSubjectOrBodyChanged',
    templateId: {
      immediate: true,
      handler(newId, oldId) {
        this.$nextTick(() => {
          const routeName = this.$route.name;
          const id = this.templateId;
          if (routeName === 'email_templates_edit' && id) {
            this.fetchTemplate();
          } else if (
            routeName === 'email_templates_new' &&
            !id &&
            oldId !== undefined
          ) {
            this.resetForm();
          }
        });
      },
    },
  },
  beforeDestroy() {
    this.pendingAttachments.forEach(att => {
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
    });
  },
  methods: {
    goBackToList() {
      this.$router.push(this.addAccountScoping('settings/email_templates'));
    },
    openAiModal() {
      this.showAiModal = true;
    },
    closeAiModal() {
      this.showAiModal = false;
    },
    applyAiPayload(data) {
      const nameRaw =
        data.template_name != null ? String(data.template_name).trim() : '';
      if (nameRaw) {
        this.form.name = nameRaw.slice(0, 255);
      }
      const subj = data.subject != null ? String(data.subject).trim() : '';
      const rawBody = data.body != null ? String(data.body).trim() : '';
      const subMap = this.normalizeSamplesMap(data.subject_variable_samples);
      const bodMap = this.normalizeSamplesMap(data.body_variable_samples);
      const ms = { val: this.getMaxNumericVarIndexFromStrings(subj, '') };
      const mb = { val: this.getMaxNumericVarIndexFromStrings('', rawBody) };
      const bumpMax = (map, acc) => {
        Object.keys(map).forEach(k => {
          const n = parseInt(k, 10);
          if (!Number.isNaN(n) && n > acc.val) acc.val = n;
        });
      };
      bumpMax(subMap, ms);
      bumpMax(bodMap, mb);
      this.subjectVariables = this.samplesArrayFromMap(ms.val, subMap);
      this.bodyVariables = this.samplesArrayFromMap(mb.val, bodMap);

      this.showVariableSamples = ms.val > 0 || mb.val > 0;
      this.form.subject = subj;
      this.form.body = rawBody;
      this.previousSubjectData = this.form.subject;
      this.previousInputBoxData = this.form.body;
      this.$nextTick(() => this.syncVariableSlots());
    },
    normalizeSamplesMap(obj) {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
      return obj;
    },
    samplesArrayFromMap(max, map) {
      const arr = [];
      for (let i = 1; i <= max; i += 1) {
        const v = map[String(i)];
        arr.push(v != null ? String(v) : '');
      }
      return arr;
    },
    async runAiSuggest() {
      const q = (this.aiQuery || '').trim();
      if (!q) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.EDITOR.AI_QUERY_INVALID'));
        return;
      }
      this.uiFlags.isAiGenerating = true;
      try {
        const { data } = await EmailTemplatesAPI.aiSuggest(q);
        this.applyAiPayload(data);
        this.closeAiModal();
        this.showAlert(this.$t('EMAIL_TEMPLATES.EDITOR.AI_SUCCESS'));
      } catch (e) {
        const code = e.response?.data?.error;
        let msg = this.$t('EMAIL_TEMPLATES.EDITOR.AI_ERROR');
        if (code === 'not_configured') {
          msg = this.$t('EMAIL_TEMPLATES.EDITOR.AI_NOT_CONFIGURED');
        } else if (code === 'invalid_query') {
          msg = this.$t('EMAIL_TEMPLATES.EDITOR.AI_QUERY_INVALID');
        }
        this.showAlert(msg);
      } finally {
        this.uiFlags.isAiGenerating = false;
      }
    },
    samplesValid() {
      return !this.allSamplesInvalid;
    },
    openTemplatePreview() {
      if (!this.samplesValid()) {
        this.showSamplesError = true;
        this.showAlert(this.$t('EMAIL_TEMPLATES.EDITOR.SAMPLE_VARS_REQUIRED'));
        return;
      }
      this.showSamplesError = false;
      this.showPreviewModal = true;
    },
    clearSamplesErrorIfValid() {
      if (this.showSamplesError && this.samplesValid()) {
        this.showSamplesError = false;
      }
    },
    onSampleVariableInput() {
      this.clearSamplesErrorIfValid();
    },
    variableLabel(index) {
      return `{{${index}}}`;
    },
    variableUsageHint(n) {
      const inS = this.numericPlaceholderPresent(n, this.form.subject);
      const inB = this.numericPlaceholderPresent(n, this.form.body);
      if (inS && inB) {
        return this.$t('EMAIL_TEMPLATES.EDITOR.VARIABLE_USAGE_SUBJECT_AND_BODY');
      }
      if (inS) {
        return this.$t('EMAIL_TEMPLATES.EDITOR.VARIABLE_USAGE_SUBJECT_ONLY');
      }
      return this.$t('EMAIL_TEMPLATES.EDITOR.VARIABLE_USAGE_BODY_ONLY');
    },
    getUnifiedSampleValue(n) {
      const inS = this.numericPlaceholderPresent(n, this.form.subject);
      const inB = this.numericPlaceholderPresent(n, this.form.body);
      if (inS && inB) {
        const a = String(this.subjectVariables[n - 1] || '').trim();
        const b = String(this.bodyVariables[n - 1] || '').trim();
        return a || b;
      }
      if (inS) return this.subjectVariables[n - 1] || '';
      return this.bodyVariables[n - 1] || '';
    },
    unifiedSampleRowInvalid(n) {
      return !String(this.getUnifiedSampleValue(n) || '').trim();
    },
    onUnifiedSampleInput(n, event) {
      const v = event.target.value;
      const inS = this.numericPlaceholderPresent(n, this.form.subject);
      const inB = this.numericPlaceholderPresent(n, this.form.body);
      if (inS) this.$set(this.subjectVariables, n - 1, v);
      if (inB) this.$set(this.bodyVariables, n - 1, v);
      this.onSampleVariableInput();
    },
    getMaxNumericVarIndex() {
      return this.getMaxNumericVarIndexFromStrings(
        this.form.subject,
        this.form.body
      );
    },
    getMaxNumericVarIndexFromStrings(subjectStr, bodyStr) {
      let max = 0;
      const re = /\{\{\s*(\d+)\s*\}\}/g;
      for (const text of [subjectStr || '', bodyStr || '']) {
        let m;
        re.lastIndex = 0;
        while ((m = re.exec(text)) !== null) {
          const n = parseInt(m[1], 10);
          if (!Number.isNaN(n) && n > max) max = n;
        }
      }
      return max;
    },
    collectNumericVariableIndices(text) {
      if (!text) return [];
      const re = /\{\{\s*(\d+)\s*\}\}/g;
      const set = new Set();
      let m;
      while ((m = re.exec(text)) !== null) {
        const n = parseInt(m[1], 10);
        if (!Number.isNaN(n) && n > 0) set.add(n);
      }
      return Array.from(set).sort((a, b) => a - b);
    },
    syncVariableSlots() {
      const maxSub = this.getMaxNumericVarIndexFromStrings(this.form.subject, '');
      const maxBod = this.getMaxNumericVarIndexFromStrings('', this.form.body);
      while (this.subjectVariables.length < maxSub) {
        this.subjectVariables.push('');
      }
      if (this.subjectVariables.length > maxSub) {
        this.subjectVariables.splice(maxSub);
      }
      while (this.bodyVariables.length < maxBod) {
        this.bodyVariables.push('');
      }
      if (this.bodyVariables.length > maxBod) {
        this.bodyVariables.splice(maxBod);
      }
      if (maxSub === 0 && maxBod === 0) {
        this.showVariableSamples = false;
      }
    },
    onSubjectOrBodyChanged() {
      const bLast = this.form.body.trim().slice(-1);
      const sLast = this.form.subject.trim().slice(-1);
      if (bLast !== '}' && sLast !== '}') {
        this.restrictionBody = false;
        this.restrictionSubject = false;
      }
      this.syncVariableSlots();
    },
    onBodyInput() {
      this.previousInputBoxData = this.form.body;
    },
    onSubjectInput() {
      this.previousSubjectData = this.form.subject;
    },
    addVariable() {
      this.showVariableSamples = true;
      const max = this.getMaxNumericVarIndex();
      if (max >= 10) return;

      const editor = this.$refs.bodyEditor;
      let consecutiveBlocked = false;
      if (
        editor &&
        typeof editor.isNumericVariableInsertionRestricted === 'function'
      ) {
        consecutiveBlocked = editor.isNumericVariableInsertionRestricted();
      } else {
        const lastChar = this.form.body.trim().slice(-1);
        consecutiveBlocked = lastChar === '}';
      }
      if (consecutiveBlocked) {
        this.restrictionBody = true;
        return;
      }

      const nextIndex = max + 1;
      const token = `{{${nextIndex}}}`;

      if (editor && typeof editor.insertTextAtSelection === 'function') {
        const ok = editor.insertTextAtSelection(token);
        if (ok) {
          this.syncVariableSlots();
          this.restrictionBody = false;
          return;
        }
      }

      this.form.body = `${this.previousInputBoxData || this.form.body} {{${nextIndex}}}`;
      this.syncVariableSlots();
      this.restrictionBody = false;
    },
    addSubjectVariable() {
      this.showVariableSamples = true;
      const max = this.getMaxNumericVarIndex();
      if (max >= 10) return;

      const nextIndex = max + 1;
      const el = this.$refs.subjectInput;
      const sub = this.form.subject || '';

      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd ?? start;
        const head = sub.slice(0, start).replace(/\s+$/, '');
        if (head.endsWith('}')) {
          this.restrictionSubject = true;
          return;
        }
        let insert = `{{${nextIndex}}}`;
        const beforeCh = start > 0 ? sub.slice(start - 1, start) : '';
        const afterCh = end < sub.length ? sub.slice(end, end + 1) : '';
        if (beforeCh && !/\s/.test(beforeCh)) {
          insert = ` ${insert}`;
        }
        if (afterCh && !/\s/.test(afterCh)) {
          insert = `${insert} `;
        }
        this.form.subject = sub.slice(0, start) + insert + sub.slice(end);
        this.$nextTick(() => {
          const pos = start + insert.length;
          el.focus();
          el.setSelectionRange(pos, pos);
        });
        this.syncVariableSlots();
        this.restrictionSubject = false;
        return;
      }

      const lastChar = sub.trim().slice(-1);
      if (lastChar === '}') {
        this.restrictionSubject = true;
        return;
      }
      this.form.subject = `${this.previousSubjectData || sub} {{${nextIndex}}}`;
      this.syncVariableSlots();
      this.restrictionSubject = false;
    },
    resolveVariablesWithSamples(text, samples) {
      if (!text) return '';
      const arr = samples || [];
      return text.replace(/{{\s*(\d+)\s*}}/g, (match, idx) => {
        const val = arr[Number(idx) - 1];
        return val || match;
      });
    },
    numericPlaceholderPresent(n, text) {
      if (!text) return false;
      const re = new RegExp(`\\{\\{\\s*${n}\\s*\\}\\}`);
      return re.test(text);
    },

    bodyVariablesFromSavedSamples(maxVar, saved) {
      const src =
        saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
      const arr = [];
      for (let i = 0; i < maxVar; i += 1) {
        const key = String(i + 1);
        const v = src[key];
        arr.push(v != null && v !== undefined ? String(v) : '');
      }
      return arr;
    },

    buildVariableSamplesPayload() {
      const max = this.getMaxNumericVarIndex();
      const out = {};
      for (let i = 1; i <= max; i += 1) {
        const inSubj = this.numericPlaceholderPresent(i, this.form.subject);
        const inBody = this.numericPlaceholderPresent(i, this.form.body);
        let val = '';
        if (inBody && inSubj) {
          val =
            String(this.bodyVariables[i - 1] || '').trim() ||
            String(this.subjectVariables[i - 1] || '').trim();
        } else if (inBody) {
          val = String(this.bodyVariables[i - 1] || '').trim();
        } else if (inSubj) {
          val = String(this.subjectVariables[i - 1] || '').trim();
        }
        out[String(i)] = val;
      }
      return out;
    },

    async fetchTemplate() {
      const id = this.templateId;
      if (!id) {
        this.uiFlags.isFetching = false;
        return;
      }
      this.uiFlags.isFetching = true;
      try {
        const { data } = await EmailTemplatesAPI.show(id);
        this.form = {
          name: data.name,
          subject: data.subject,
          body: data.body || '',
          visibility: data.visibility || 'global',
        };
        this.existingAttachments = Array.isArray(data.attachments) ? data.attachments : [];
        this.pendingAttachments = [];
        this.removedAttachmentIds = [];
        const maxSub = this.getMaxNumericVarIndexFromStrings(
          this.form.subject,
          ''
        );
        const maxBod = this.getMaxNumericVarIndexFromStrings('', this.form.body);
        this.showVariableSamples = maxSub > 0 || maxBod > 0;
        this.subjectVariables = this.bodyVariablesFromSavedSamples(
          maxSub,
          data.variable_samples
        );
        this.bodyVariables = this.bodyVariablesFromSavedSamples(
          maxBod,
          data.variable_samples
        );
        this.previousInputBoxData = this.form.body;
        this.previousSubjectData = this.form.subject;
        this.showSamplesError = false;
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.ERROR_LOAD'));
      } finally {
        this.uiFlags.isFetching = false;
      }
    },
    resetForm() {
      this.form = { name: '', subject: '', body: '', visibility: 'global' };
      this.existingAttachments = [];
      this.pendingAttachments = [];
      this.removedAttachmentIds = [];
      this.subjectVariables = [];
      this.bodyVariables = [];
      this.showVariableSamples = false;
      this.restrictionBody = false;
      this.restrictionSubject = false;
      this.previousInputBoxData = '';
      this.previousSubjectData = '';
      this.showSamplesError = false;
      this.$v.$reset();
    },

    triggerAttachmentInput() {
      this.$refs.attachmentInput?.click();
    },
    async onAttachmentSelected(event) {
      const files = event.target.files;
      if (!files?.length) return;
      for (let i = 0; i < files.length; i++) {
        await this.addAttachment(files[i]);
      }
      if (this.$refs.attachmentInput) this.$refs.attachmentInput.value = '';
    },
    async addAttachment(file) {
      try {
        const { data } = await EmailTemplatesAPI.attachFile(file);
        this.pendingAttachments.push({ blobId: data.blob_id, filename: file.name });
      } catch (e) {
        this.showAlert(this.$t('EMAIL_TEMPLATES.ATTACH_UPLOAD_ERROR'));
      }
    },
    removeExistingAttachment(id) {
      this.removedAttachmentIds.push(id);
      this.existingAttachments = this.existingAttachments.filter(a => a.id !== id);
    },
    removePendingAttachment(index) {
      const att = this.pendingAttachments[index];
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      this.pendingAttachments.splice(index, 1);
    },

    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      if (!this.samplesValid()) {
        this.showSamplesError = true;
        this.showAlert(this.$t('EMAIL_TEMPLATES.EDITOR.SAMPLE_VARS_REQUIRED'));
        return;
      }
      this.showSamplesError = false;
      this.uiFlags.isSaving = true;
      try {
        const payload = {
          email_template: {
            name: this.form.name.trim(),
            subject: this.form.subject.trim(),
            body: this.form.body,
            variable_samples: this.buildVariableSamplesPayload(),
          },
          visibility: this.form.visibility,
        };
        if (this.pendingAttachments.length) {
          payload.attachment_blob_ids = this.pendingAttachments.map(a => a.blobId);
        }
        if (this.isEdit && this.removedAttachmentIds.length) {
          payload.removed_attachment_ids = this.removedAttachmentIds;
        }
        if (this.isEdit) {
          await EmailTemplatesAPI.update(this.templateId, payload);
          this.showAlert(this.$t('EMAIL_TEMPLATES.UPDATE_SUCCESS'));
        } else {
          await EmailTemplatesAPI.create(payload);
          this.showAlert(this.$t('EMAIL_TEMPLATES.CREATE_SUCCESS'));
        }
        this.$router.push(this.addAccountScoping('settings/email_templates'));
      } catch (e) {
        const msg = e.response?.data?.error || this.$t('EMAIL_TEMPLATES.SAVE_ERROR');
        this.showAlert(Array.isArray(msg) ? msg.join(' ') : msg);
      } finally {
        this.uiFlags.isSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.email-template-editor-page {
  background-color: var(--color-background-light);
  width: 100%;
  min-height: 0;
}

body.dark .email-template-editor-page {
  background-color: #151718;
}

.email-template-editor {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.viewer-header {
  background-color: var(--white);
  border-bottom: 1px solid var(--color-border);
  padding: var(--space-normal) var(--space-large);
  position: relative;
}

body.dark .viewer-header {
  background-color: #151718;
  border-bottom-color: var(--color-border);
}

.viewer-header-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-small);

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-normal);
  }
}

.header-title {
  margin: 0;
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
}

body.dark .header-title {
  color: #f3f4f6;
}

.email-template-back-btn {
  background-color: #40518a !important;
  color: white !important;
  padding: 10px 14px !important;
  line-height: 1.2 !important;
  border-radius: var(--border-radius-medium) !important;
  border: 1px solid #334074 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.content-wrapper {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.email-template-editor-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-small);
  padding: var(--space-small) 0 var(--space-normal);

  .preview-title {
    margin: 0;
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    color: var(--color-heading);
  }

  .preview-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-small);
    align-items: center;
  }
}

body.dark .preview-header .preview-title {
  color: #f3f4f6;
}

.template-panel {
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &--body {
    padding-bottom: var(--space-small);
  }

  &--attachments {
    margin-bottom: 0;
  }
}

body.dark .template-panel {
  background-color: #1a1d1f;
  border-color: var(--color-border);
}

.section-label {
  display: block;
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
  margin-bottom: 0;

  &--spaced {
    margin-top: var(--space-normal);
  }
}

body.dark .section-label {
  color: #e5e7eb;
}

.panel-input {
  width: 100%;
  margin-top: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border);
  background-color: var(--color-background);
  font-size: var(--font-size-small);
  color: var(--color-heading);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: var(--color-body-light);
  }

  &:focus {
    outline: none;
    border-color: var(--w-500);
    box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.2);
  }

  &--error {
    border-color: var(--r-400);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
  }
}

body.dark .panel-input {
  background-color: #252a2e;
  color: #f3f4f6;
}

.form-error {
  display: block;
  margin-top: var(--space-micro);
  font-size: var(--font-size-mini);
  color: var(--r-500);
}

.form-hint {
  font-size: var(--font-size-mini);
  color: var(--color-body);
  margin: var(--space-small) 0 var(--space-small);
}

.subject-row {
  display: flex;
  gap: var(--space-small);
}

.subject-input {
  flex: 1;
}

.subject-char-count {
  margin-top: var(--space-micro);
  font-size: var(--font-size-mini);
  text-align: right;
  color: var(--color-body-light);

  &--warn {
    color: var(--y-600);
  }

  &--limit {
    color: var(--r-500);
  }
}

.body-editor-wrap {
  margin-top: var(--space-small);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border);
  background-color: var(--white);
  overflow: hidden;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &--error {
    border-color: var(--r-400);
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.12);
  }

  &:focus-within {
    border-color: var(--w-500);
    box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.2);
  }

  /* Editor root also has global .message-editor (border + radius); strip it so only .body-editor-wrap draws one frame */
  :deep(.email-template-body-editor.editor-root) {
    box-sizing: border-box;
    padding-bottom: var(--space-normal);
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  :deep(.ProseMirror-menubar-wrapper) {
    border-bottom: 1px solid var(--color-border);
    position: relative !important;
    top: auto !important;
    overflow: visible;
  }

  :deep(.ProseMirror-menubar) {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    right: auto !important;
    padding: 0.25rem 0.5rem !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100%;
    background: inherit;
    z-index: auto;
    transform: none !important;
  }

  :deep(.ProseMirror-woot-style) {
    min-height: 9rem !important;
    max-height: 20rem;
    overflow-y: auto;
    padding: 0.625rem 0.875rem 1.125rem;
    background-color: var(--color-background);
  }
}

body.dark .body-editor-wrap {
  background-color: #1a1d1f;

  :deep(.ProseMirror-woot-style) {
    background-color: #252a2e;
  }
}

.body-restriction-msg {
  margin-top: var(--space-micro);
  font-size: var(--font-size-mini);
  color: var(--r-500);
}

.add-variable-row {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-small);

  &--subject {
    margin-top: var(--space-small);
  }
}

.add-variable-btn {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--w-500);
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: var(--w-600);
  }

  &:disabled {
    color: var(--color-body-light);
    cursor: not-allowed;
  }
}

.variable-samples {
  margin-top: var(--space-normal);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border);
  background-color: var(--color-background);
  overflow: hidden;
}

.variable-samples--unified {
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
}

.variable-samples__intro {
  margin: 0 0 var(--space-normal);
  font-size: var(--font-size-small);
  color: var(--color-body);
  line-height: 1.45;
}

.variable-samples__list {
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  overflow: hidden;
  margin-top: var(--space-small);
}

.variable-samples__section-hint {
  margin: 0;
  padding: var(--space-small) var(--space-normal);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
  border-bottom: 1px solid var(--color-border);
}

.variable-sample-row {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
}

.variable-sample-row--unified {
  align-items: center;
}

.variable-sample-row__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  min-width: 6.5rem;
}

.variable-sample-label {
  flex-shrink: 0;
  width: 2.5rem;
  text-align: center;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--color-body);
}

.variable-sample-label--token {
  width: auto;
  min-width: 2.75rem;
  text-align: left;
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
}

.variable-sample-usage {
  font-size: var(--font-size-mini);
  color: var(--color-body-light);
  line-height: 1.2;
}

.variable-sample-input {
  flex: 1;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border);
  background-color: var(--white);
  padding: var(--space-micro) var(--space-small);
  font-size: var(--font-size-small);
  color: var(--color-heading);

  &:focus {
    outline: none;
    border-color: var(--w-500);
    box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.15);
  }
}

body.dark .variable-sample-input {
  background-color: #252a2e;
  color: #f3f4f6;
}

.visibility-options {
  display: flex;
  gap: var(--space-normal);
  flex-wrap: wrap;
  margin-top: var(--space-small);
}

.visibility-option {
  flex: 1;
  min-width: 10rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-small) var(--space-normal);
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--color-border);
  background-color: var(--color-background);
  cursor: pointer;
  font: inherit;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: var(--w-400);
  }

  &--active {
    border-color: var(--w-500);
    background-color: rgba(64, 81, 138, 0.06);
  }

  &__label {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--color-heading);
  }

  &__desc {
    font-size: var(--font-size-mini);
    color: var(--color-body);
  }
}

body.dark .visibility-option--active {
  background-color: rgba(64, 81, 138, 0.15);
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.attachment-item-preview {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small);
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  }
}

.attachment-thumbnail {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white);
  border-radius: var(--border-radius-small);
}

.attachment-name {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-heading);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-item__remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: var(--border-radius-small);
  background: transparent;
  color: var(--color-body-light);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    color: var(--r-500);
    background-color: rgba(239, 68, 68, 0.08);
  }
}

.bottom-actions {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-small);
  padding-top: var(--space-normal);
  margin-top: var(--space-small);
  border-top: 1px solid var(--color-border);
}

body.dark .bottom-actions {
  border-top-color: var(--color-border);
}

.variable-samples__error-msg {
  margin: 0 0 var(--space-small);
  padding: var(--space-small) var(--space-normal);
  font-size: var(--font-size-mini);
  color: var(--r-600);
  background-color: var(--r-50);
  border-radius: var(--border-radius-small);
}

.variable-samples--error {
  border-color: var(--r-400) !important;
  box-shadow: 0 0 0 1px var(--r-200);
}

.variable-sample-input--error {
  border-color: var(--r-400) !important;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.12);
}

.ai-modal-content {
  box-sizing: border-box;
  width: 100%;
  padding: var(--space-large);
}

.ai-modal__title {
  margin: 0 0 var(--space-normal);
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  color: var(--color-heading);
}

.ai-modal__label {
  display: block;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-body);
  margin-bottom: var(--space-small);
}

.ai-modal__required {
  color: var(--r-500);
  font-weight: var(--font-weight-bold);
  margin-left: 2px;
}

.ai-modal__textarea {
  width: 100%;
  padding: var(--space-small) var(--space-normal);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border);
  background-color: var(--color-background);
  color: var(--color-heading);
  font-size: var(--font-size-small);
  line-height: 1.5;
  resize: vertical;
  min-height: 7rem;

  &:focus {
    outline: none;
    border-color: var(--w-500);
    box-shadow: 0 0 0 2px rgba(64, 81, 138, 0.2);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

body.dark .ai-modal__textarea {
  background-color: #252a2e;
  color: #f3f4f6;
}

.ai-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-normal);
}

</style>
